export interface RttSample {
  ts: number
  rtt: number
}

export interface RttStats {
  min: number
  max: number
  avg: number
  p95: number
  count: number
}

const ZERO_STATS: RttStats = { min: 0, max: 0, avg: 0, p95: 0, count: 0 }
const AUTO_SAVE_INTERVAL = 10
const STATS_CACHE_TTL = 2000

export class RttMonitor {
  private readonly capacity: number
  private readonly storageKey: string
  private ts: Float64Array
  private rtt: Float64Array
  private head: number = 0
  private count: number = 0
  private recordsSinceSave: number = 0
  private recentCache: { stats: RttStats; windowMs: number; computedAt: number } | null = null

  constructor(capacity: number = 1000, storageKey: string = 'rtt-history') {
    this.capacity = capacity
    this.storageKey = storageKey
    this.ts = new Float64Array(capacity)
    this.rtt = new Float64Array(capacity)
    this.load()
  }

  record(rttMs: number): void {
    if (!Number.isFinite(rttMs) || rttMs < 0) return
    const idx = this.head
    this.ts[idx] = Date.now()
    this.rtt[idx] = rttMs
    this.head = (this.head + 1) % this.capacity
    if (this.count < this.capacity) this.count++
    this.recentCache = null
    this.recordsSinceSave++
    if (this.recordsSinceSave >= AUTO_SAVE_INTERVAL) {
      this.save()
      this.recordsSinceSave = 0
    }
  }

  getSamples(): RttSample[] {
    if (this.count === 0) return []
    const result: RttSample[] = new Array(this.count)
    const start = this.count < this.capacity ? 0 : this.head
    for (let i = 0; i < this.count; i++) {
      const idx = (start + i) % this.capacity
      result[i] = { ts: this.ts[idx], rtt: this.rtt[idx] }
    }
    return result
  }

  getStats(): RttStats {
    return this.computeStats(this.getAllRttValues())
  }

  getRecentStats(windowMs: number): RttStats {
    const now = Date.now()
    if (this.recentCache &&
        this.recentCache.windowMs === windowMs &&
        now - this.recentCache.computedAt < STATS_CACHE_TTL) {
      return this.recentCache.stats
    }
    const cutoff = now - windowMs
    const values: number[] = []
    const start = this.count < this.capacity ? 0 : this.head
    for (let i = 0; i < this.count; i++) {
      const idx = (start + i) % this.capacity
      if (this.ts[idx] >= cutoff) {
        values.push(this.rtt[idx])
      }
    }
    const stats = this.computeStats(values)
    this.recentCache = { stats, windowMs, computedAt: now }
    return stats
  }

  clear(): void {
    this.head = 0
    this.count = 0
    this.recordsSinceSave = 0
    this.recentCache = null
  }

  save(): void {
    try {
      if (typeof localStorage === 'undefined' || !localStorage) return
      const samples = this.getSamples()
      localStorage.setItem(this.storageKey, JSON.stringify(samples))
    } catch {
      // Silently ignore storage errors
    }
  }

  private load(): void {
    try {
      if (typeof localStorage === 'undefined' || !localStorage) return
      const raw = localStorage.getItem(this.storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) return
      const maxLoad = Math.min(parsed.length, this.capacity)
      const startIdx = parsed.length - maxLoad
      for (let i = startIdx; i < parsed.length; i++) {
        const entry = parsed[i]
        if (typeof entry.ts !== 'number' || typeof entry.rtt !== 'number') continue
        const idx = this.head
        this.ts[idx] = entry.ts
        this.rtt[idx] = entry.rtt
        this.head = (this.head + 1) % this.capacity
        if (this.count < this.capacity) this.count++
      }
    } catch {
      // Corrupted data — start fresh
    }
  }

  private getAllRttValues(): number[] {
    if (this.count === 0) return []
    const values: number[] = new Array(this.count)
    const start = this.count < this.capacity ? 0 : this.head
    for (let i = 0; i < this.count; i++) {
      values[i] = this.rtt[(start + i) % this.capacity]
    }
    return values
  }

  private computeStats(values: number[]): RttStats {
    if (values.length === 0) return { ...ZERO_STATS }
    const sorted = values.slice().sort((a, b) => a - b)
    const count = sorted.length
    const sum = sorted.reduce((a, b) => a + b, 0)
    const p95Index = Math.min(Math.ceil(0.95 * count) - 1, count - 1)
    return {
      min: sorted[0],
      max: sorted[count - 1],
      avg: sum / count,
      p95: sorted[p95Index],
      count,
    }
  }
}
